import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { subscribeToMarketValueChanges, fetchRecentMarketValueChanges, unsubscribeFromChannel } from '../../services/realtimeService';
import ValueTrackingChart from './components/ValueTrackingChart';
import ComparativeAnalysisPanel from './components/ComparativeAnalysisPanel';
import MarketSentimentCard from './components/MarketSentimentCard';
import ValueAlertCard from './components/ValueAlertCard';
import PlayerValueCard from './components/PlayerValueCard';
import AnalyticsFilters from './components/AnalyticsFilters';

const MarketValueAnalytics = () => {
  const { user } = useAuth();
  const [marketValueData, setMarketValueData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liveUpdates, setLiveUpdates] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [filters, setFilters] = useState({
    position: 'all',
    league: 'all',
    ageGroup: 'all',
    valueRange: 'all',
    timeframe: '30'
  });

  // Fetch initial market value data
  useEffect(() => {
    const loadMarketData = async () => {
      setLoading(true);
      const { data, error } = await fetchRecentMarketValueChanges(parseInt(filters?.timeframe));
      if (!error && data) {
        setMarketValueData(data);
      }
      setLoading(false);
    };
    loadMarketData();
  }, [filters?.timeframe]);

  // Subscribe to real-time market value changes
  useEffect(() => {
    const channel = subscribeToMarketValueChanges((payload) => {
      if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE') {
        setLiveUpdates(prev => [{
          id: payload?.new?.id,
          playerId: payload?.new?.player_id,
          marketValue: payload?.new?.market_value,
          valuationDate: payload?.new?.valuation_date,
          timestamp: new Date()?.toISOString()
        }, ...prev]?.slice(0, 10));
      }
    });

    return () => {
      if (channel) unsubscribeFromChannel(channel);
    };
  }, []);

  // Filter market value data
  const filteredData = useMemo(() => {
    return marketValueData?.filter(item => {
      if (filters?.position !== 'all' && item?.player?.primaryPosition !== filters?.position) return false;
      if (filters?.league !== 'all' && item?.player?.currentLeague !== filters?.league) return false;
      
      if (filters?.ageGroup !== 'all') {
        const birthDate = new Date(item?.player?.dateOfBirth);
        const age = new Date()?.getFullYear() - birthDate?.getFullYear();
        if (filters?.ageGroup === 'young' && (age < 18 || age > 23)) return false;
        if (filters?.ageGroup === 'prime' && (age < 24 || age > 29)) return false;
        if (filters?.ageGroup === 'experienced' && age < 30) return false;
      }
      
      if (filters?.valueRange !== 'all') {
        const value = item?.marketValue || 0;
        if (filters?.valueRange === 'low' && value > 5000000) return false;
        if (filters?.valueRange === 'medium' && (value <= 5000000 || value > 20000000)) return false;
        if (filters?.valueRange === 'high' && value <= 20000000) return false;
      }
      
      return true;
    });
  }, [marketValueData, filters]);

  // Group data by player for tracking
  const playerValueHistory = useMemo(() => {
    const grouped = {};
    filteredData?.forEach(item => {
      if (!grouped?.[item?.playerId]) {
        grouped[item?.playerId] = {
          player: item?.player,
          history: []
        };
      }
      grouped?.[item?.playerId]?.history?.push({
        date: item?.valuationDate,
        value: item?.marketValue,
        currency: item?.currency
      });
    });
    
    // Sort history by date
    Object.keys(grouped)?.forEach(playerId => {
      grouped?.[playerId]?.history?.sort((a, b) => new Date(a?.date) - new Date(b?.date));
    });
    
    return grouped;
  }, [filteredData]);

  // Calculate market insights
  const marketInsights = useMemo(() => {
    const players = Object.values(playerValueHistory);
    const totalPlayers = players?.length;
    
    let increasing = 0;
    let decreasing = 0;
    let stable = 0;
    
    players?.forEach(({ history }) => {
      if (history?.length >= 2) {
        const latest = history?.[history?.length - 1]?.value;
        const previous = history?.[history?.length - 2]?.value;
        const change = ((latest - previous) / previous) * 100;
        
        if (change > 5) increasing++;
        else if (change < -5) decreasing++;
        else stable++;
      }
    });
    
    return {
      totalPlayers,
      increasing,
      decreasing,
      stable,
      avgChange: totalPlayers > 0 ? ((increasing - decreasing) / totalPlayers * 100)?.toFixed(1) : 0
    };
  }, [playerValueHistory]);

  const handlePlayerSelect = (playerId) => {
    setSelectedPlayers(prev => {
      if (prev?.includes(playerId)) {
        return prev?.filter(id => id !== playerId);
      } else if (prev?.length < 4) {
        return [...prev, playerId];
      }
      return prev;
    });
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-12 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Icon name="TrendingUp" size={28} className="text-primary" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground">Market Value Analytics</h1>
              <p className="text-muted-foreground mt-1">Real-time player valuation insights and market trend analysis</p>
            </div>
          </div>
          
          {liveUpdates?.length > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-success/10 border border-success/20 rounded-lg">
              <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
              <span className="text-sm font-medium text-success">Live Updates Active</span>
              <span className="text-xs text-muted-foreground">• {liveUpdates?.length} recent changes</span>
            </div>
          )}
        </div>

        {/* Market Insights Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Total Players Tracked</span>
              <Icon name="Users" size={20} className="text-primary" />
            </div>
            <p className="text-3xl font-bold text-foreground">{marketInsights?.totalPlayers}</p>
          </div>
          
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Value Increasing</span>
              <Icon name="ArrowUp" size={20} className="text-success" />
            </div>
            <p className="text-3xl font-bold text-success">{marketInsights?.increasing}</p>
          </div>
          
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Value Decreasing</span>
              <Icon name="ArrowDown" size={20} className="text-error" />
            </div>
            <p className="text-3xl font-bold text-error">{marketInsights?.decreasing}</p>
          </div>
          
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Market Sentiment</span>
              <Icon name="Activity" size={20} className="text-accent" />
            </div>
            <p className="text-3xl font-bold text-foreground">{marketInsights?.avgChange}%</p>
          </div>
        </div>

        {/* Filters */}
        <AnalyticsFilters filters={filters} onFilterChange={handleFilterChange} />

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Value Tracking Chart - Takes 2 columns */}
          <div className="lg:col-span-2">
            <ValueTrackingChart 
              data={Object.values(playerValueHistory)?.slice(0, 10)} 
              timeframe={filters?.timeframe}
            />
          </div>
          
          {/* Market Sentiment */}
          <div>
            <MarketSentimentCard insights={marketInsights} />
          </div>
        </div>

        {/* Live Value Alerts */}
        {liveUpdates?.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <Icon name="Bell" size={20} />
              Live Value Alerts
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {liveUpdates?.slice(0, 6)?.map((update, index) => (
                <ValueAlertCard key={update?.id || index} update={update} />
              ))}
            </div>
          </div>
        )}

        {/* Player Value Cards */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
              <Icon name="Target" size={20} />
              Player Valuations
            </h2>
            <span className="text-sm text-muted-foreground">
              {selectedPlayers?.length > 0 && `${selectedPlayers?.length} selected for comparison`}
            </span>
          </div>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">Loading market data...</p>
            </div>
          ) : Object.keys(playerValueHistory)?.length === 0 ? (
            <div className="text-center py-12 bg-card border border-border rounded-lg">
              <Icon name="TrendingUp" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No Market Data Available</h3>
              <p className="text-muted-foreground">No player valuations found for the selected filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(playerValueHistory)?.slice(0, 12)?.map(([playerId, data]) => (
                <PlayerValueCard 
                  key={playerId}
                  playerId={playerId}
                  player={data?.player}
                  history={data?.history}
                  isSelected={selectedPlayers?.includes(playerId)}
                  onSelect={handlePlayerSelect}
                />
              ))}
            </div>
          )}
        </div>

        {/* Comparative Analysis */}
        {selectedPlayers?.length >= 2 && (
          <ComparativeAnalysisPanel 
            selectedPlayers={selectedPlayers?.map(id => playerValueHistory?.[id])?.filter(Boolean)}
            onClearSelection={() => setSelectedPlayers([])}
          />
        )}
      </main>
    </div>
  );
};

export default MarketValueAnalytics;